// The MapObject type and its hover card, cut from apps/admin/src/features/map-view/ui/map-view.tsx
// (PRD #875). The rest of that module is admin's dashboard map, which govdash does not use.
import { Tooltip } from "react-leaflet";
import { getEnumName } from "@racoon/lib";
import { cn } from "@racoon/ui";
import {
  pickupPalette,
  pinTooltipOffset,
  fanOffsetPx,
  siteCardPinHtml,
} from "../model/constants/icons";
import { jobStatusTextClass } from "../model/constants/job-status-colors";
import { locationTypeStyle } from "../model/constants/location-types";
import type { StepJob } from "../model/helpers/step-pins";
import { Namespace, useLang, useT } from "@racoon/i18n";
import { ConnectProto } from "@racoon/api";

export type InfoType = "route" | "truck" | "job" | "pickup" | "depot";

export type MapObject = {
  name: string;
  type: InfoType;
  id: number | string;
  lat: number;
  lon: number;
  angle?: number;
  route?: ConnectProto.Route;
  companyName?: string;
  localIndex?: number;
  /** Printed on the pin instead of localIndex. */
  pinLabel?: string;
  /** The job a step pin stands for. */
  jobId?: bigint;
  /** Colours the pin by this job instead of by its site's latest job. */
  jobStatus?: ConnectProto.JobStatus;
  /** Position among pins sharing one site; they fan out around it. */
  fan?: { index: number; count: number };
  /** A collapsed pin: every step it stands for, and how to fan it back out. */
  steps?: string[];
  /** A collapsed pin: the jobs of those steps, lowest step first. */
  stepJobs?: StepJob[];
  onExpand?: () => void;
  /** Set on a collapsed pin whose steps serve several customers. */
  customerCount?: number;
  /** Only this site's geofence, drawn under a fan of step pins. */
  shapeOnly?: boolean;
  /** Step pins: the site they belong to, which their folding is keyed on. */
  siteKey?: string;
  status?: string | ConnectProto.DriverOnShiftStatus;
  pickupType?: ConnectProto.PickupLocationType | string;
  containerGroups?: ConnectProto.ContainerGroup[];
  hasOrders?: boolean;
  hasUnassignedOrders?: boolean;
  truckType?: string;
  hasShift?: boolean;
  /** Trucks only: the parent-tier org the travel-path and stops RPCs match on. */
  ownerOrganizationId?: string | number | bigint;
  /** GeoJSON from PickupLocation.geometry; drawn instead of the pin when legible. */
  geometry?: unknown;
  /** Depots only: which kind of location, for its icon and hover card. */
  locationType?: ConnectProto.LocationType;
  /** Depots only: screen offset that keeps it clear of a nearby location. */
  nudge?: [number, number];
};

// Pinned to tooltipPane: inside the `pickups` pane a marker's computed z-index
// beats a tooltip's `auto`, so the pin would cover its own label.
const customersKey = (count: number, lang: string) => {
  const form = new Intl.PluralRules(lang).select(count);
  return form === "one"
    ? "stepCustomersOne"
    : form === "few"
      ? "stepCustomersFew"
      : "stepCustomersMany";
};

const CARD_ARROW = "rgba(0, 0, 0, 0.75)";

// A location's card opens around its marker: the card's icon lands on the marker's
// (28px, top of the 32px box anchored bottom-centre). x also cancels Leaflet's 6px margin.
const LOCATION_ICON_PX = 28;
const locationCardOffset = ([dx, dy] = [0, 0]): [number, number] => [
  dx - (8 + LOCATION_ICON_PX / 2 + 6),
  dy - 18,
];

const MAX_CARD_JOBS = 3;

const JobStatusLine = ({
  step,
  jobId,
  status,
}: {
  step?: string;
  jobId?: bigint;
  status?: ConnectProto.JobStatus;
}) => {
  const t = useT();
  const toneText = status != null ? jobStatusTextClass(status) : undefined;
  const statusName = getEnumName(ConnectProto.JobStatusSchema, status);
  return (
    <div
      className={cn("text-xs font-medium leading-4", toneText)}
      style={toneText ? undefined : { color: "#2563EB" }}
    >
      {step && <span className="mr-1.5 text-white/60">#{step}</span>}
      {jobId != null && `Z${String(jobId)}`}
      {statusName && (
        <>
          <span className="mx-1.5 opacity-60">|</span>
          {t(Namespace.Enum, statusName)}
        </>
      )}
    </div>
  );
};

/** Figma «Location Hover Tooltip»: a dark card with the kind's icon, name and label. */
export const MapObjectTooltip = ({ item }: { item: MapObject }) => {
  const t = useT();
  const { lang } = useLang();
  const arrowDx = fanOffsetPx(item);
  const fanned = item.fan != null && item.fan.count > 1;
  const isLocation = item.type.toString() === "depot";
  const location = isLocation ? locationTypeStyle(item.locationType) : undefined;
  const secondary = "text-xs leading-4 text-white/60";
  return (
    // The card of a fan stays centred over the fan; only its arrow follows the
    // hovered pin, so Leaflet's own centred arrow is hidden.
    <Tooltip
      pane="tooltipPane"
      direction={location ? "right" : "top"}
      offset={location ? locationCardOffset(item.nudge) : pinTooltipOffset(item)}
      className={cn(
        "!rounded-lg !border-0 !bg-black/75 !p-2 !text-white !shadow-none",
        "before:!border-t-black/75",
        (fanned || location) && "before:!hidden",
        // Opens out of the marker: starts as just the icon (padding + icon + padding).
        location && "animate-unroll-right [--unroll-from:44px] motion-reduce:animate-none",
      )}
    >
      <div className="flex items-center gap-2">
        {location ? (
          <location.Icon
            width={LOCATION_ICON_PX}
            height={LOCATION_ICON_PX}
            className="shrink-0"
            style={{ color: location.color }}
          />
        ) : (
          <span
            className="flex h-8 w-8 shrink-0 items-center justify-center"
            dangerouslySetInnerHTML={{
              __html: siteCardPinHtml(pickupPalette(item, false, item.jobStatus)),
            }}
          />
        )}
        <div className="min-w-0">
          <div className="text-sm font-medium leading-5">
            {item.name || `${t(Namespace.Routes, "point")} ${item.localIndex ?? ""}`.trim()}
          </div>
          {location?.labelKey && (
            <div
              className="text-xs font-medium uppercase leading-4"
              style={{ color: location.color }}
            >
              {t(Namespace.GpsTracking, location.labelKey)}
            </div>
          )}
          {/* A shared site's pin already prints its steps; the card names their jobs. */}
          {item.stepJobs && item.stepJobs.length > 1 ? (
            <>
              {item.stepJobs.slice(0, MAX_CARD_JOBS).map((job, i) => (
                <JobStatusLine key={i} step={job.step} jobId={job.jobId} status={job.jobStatus} />
              ))}
              {item.stepJobs.length > MAX_CARD_JOBS && (
                <div className={secondary}>
                  {t(Namespace.Infopanel, "siteJobs.more")} {item.stepJobs.length - MAX_CARD_JOBS}
                </div>
              )}
            </>
          ) : (
            item.jobId != null && <JobStatusLine jobId={item.jobId} status={item.jobStatus} />
          )}
          {item.customerCount ? (
            <div className={secondary}>
              {t(Namespace.GpsTracking, customersKey(item.customerCount, lang), {
                count: item.customerCount,
              })}
            </div>
          ) : (
            item.companyName && <div className={secondary}>{item.companyName}</div>
          )}
        </div>
      </div>
      {fanned && (
        <span
          className="pointer-events-none absolute"
          style={{
            left: `calc(50% + ${arrowDx}px)`,
            bottom: -12,
            marginLeft: -6,
            border: "6px solid transparent",
            borderTopColor: CARD_ARROW,
          }}
        />
      )}
    </Tooltip>
  );
};
